"use client";

import { ReactNode } from "react";
import {
  SimpleAuthProvider,
  useSimpleAuth,
  useSimpleAuthOptional,
} from "./SimpleAuthProvider";
import {
  UserDataProvider,
  useUserData,
  useUserDataOptional,
} from "./UserDataProvider";

// ============================================================================
// OPTIMAL AUTH PROVIDER - BEST PRACTICES IMPLEMENTATION
// ============================================================================
//
// PURPOSE: Combine auth and user data (Hub, UnifiedTime, next-email-client, etc.)
// ARCHITECTURE:
//
// ┌─────────────────────────────────────────────────────────┐
// │                OptimalAuthProvider                      │
// │  ┌─────────────────┐    ┌─────────────────────────────┐ │
// │  │ SimpleAuth      │    │      UserDataProvider       │ │
// │  │ - Session       │───▶│  - App user data (lazy)     │ │
// │  │ - Immediate     │    │  - Role permissions         │ │
// │  │ - No timeouts   │    │  - Background loading       │ │
// │  └─────────────────┘    └─────────────────────────────┘ │
// └─────────────────────────────────────────────────────────┘
//
// Multi-tenant org context (`OrganizationProvider`) is NOT included here: the Hub
// and other main apps do not use the nonprofit-hub `organizations` schema. Apps
// that need org switching (e.g. nonprofit-hub) wrap `OrganizationProvider` themselves.
//
// BENEFITS:
// - ✅ Auth resolves immediately (no timeouts)
// - ✅ User data loads in background (non-blocking)
// - ✅ Separated concerns (maintainable)
// - ✅ Error resilient (auth works even if user data fails)
// - ✅ Follows Supabase + React best practices
//
// CONSUMER APPS: Use isInitialized for first paint (session known). Use isLoading
// only where appUser/roles are required; prefer skeletons when isInitialized && isLoading.
//
// ============================================================================

interface OptimalAuthProviderProps {
  children: ReactNode;
  supabaseClient: any;
}

export function OptimalAuthProvider({
  children,
  supabaseClient,
}: OptimalAuthProviderProps) {
  return (
    <SimpleAuthProvider supabaseClient={supabaseClient}>
      <UserDataProvider supabaseClient={supabaseClient}>{children}</UserDataProvider>
    </SimpleAuthProvider>
  );
}

// ============================================================================
// UNIFIED HOOK (Backward Compatibility)
// ============================================================================

export function useOptimalAuth() {
  const auth = useSimpleAuth();
  const userData = useUserData();

  return {
    // Auth state (immediate)
    session: auth.session,
    user: auth.user,
    isAuthenticated: auth.isAuthenticated,
    isInitialized: auth.isInitialized,
    signOut: auth.signOut,

    // User data (lazy loaded)
    appUser: userData.appUser,
    isLoading: userData.isLoading,
    error: userData.error,
    refreshUser: userData.refreshUser,

    // Role helpers
    isEmployee: userData.isEmployee,
    isAdmin: userData.isAdmin,
    isClientUser: userData.isClientUser,
    userRole: userData.userRole,
  };
}

// ============================================================================
// MIGRATION HELPER (Drop-in replacement for UniversalAuth)
// ============================================================================

export function UniversalAuthProvider({
  children,
  supabaseClient,
}: {
  children: ReactNode;
  supabaseClient: any;
}) {
  return (
    <OptimalAuthProvider supabaseClient={supabaseClient}>
      {children}
    </OptimalAuthProvider>
  );
}

export function useUniversalAuth() {
  return useOptimalAuth();
}

// ============================================================================
// NON-THROWING VARIANT FOR BOUNDARY COMPONENTS
// ============================================================================
//
// not-found.tsx / error.tsx are rendered by Next.js as boundaries and can be
// invoked OUTSIDE the app provider tree (e.g. a stale/foreign Server Action POST
// that resolves to the 404 boundary during server-rendering). The standard
// useUniversalAuth() throws there because SimpleAuthProvider/UserDataProvider
// aren't mounted. This variant degrades to a logged-out shape instead of
// crashing the boundary. Use it ONLY in boundary pages — normal app code should
// use useUniversalAuth() so a genuinely-missing provider still surfaces.
export function useUniversalAuthOptional() {
  const auth = useSimpleAuthOptional();
  const userData = useUserDataOptional();

  return {
    // Auth state (immediate) — logged-out defaults when no provider
    session: auth?.session ?? null,
    user: auth?.user ?? null,
    isAuthenticated: auth?.isAuthenticated ?? false,
    isInitialized: auth?.isInitialized ?? false,
    signOut: auth?.signOut ?? (async () => {}),

    // User data (lazy loaded)
    appUser: userData?.appUser ?? null,
    isLoading: userData?.isLoading ?? false,
    error: userData?.error ?? null,
    refreshUser: userData?.refreshUser ?? (async () => {}),

    // Role helpers
    isEmployee: userData?.isEmployee ?? false,
    isAdmin: userData?.isAdmin ?? false,
    isClientUser: userData?.isClientUser ?? false,
    userRole: userData?.userRole ?? null,
  };
}

// Export individual providers for flexibility
export {
  SimpleAuthProvider,
  useSimpleAuth,
  useSimpleAuthOptional,
} from "./SimpleAuthProvider";
export {
  UserDataProvider,
  useUserData,
  useUserDataOptional,
} from "./UserDataProvider";
